import { toColor, toIconName, VisualPickerSheet } from "@salestrak/ui";

import { useTeamMenuActions } from "#app/components/teams/team-menu-items";
import { useWorkflowMenuActions } from "#app/components/workflows/workflow-menu-items";
import { appearanceSheet } from "#app/lib/appearance-sheet-context";
import { useAppData } from "#app/lib/data";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { resolveTeamGlyph } from "#app/lib/hooks/use-team-detail";

/**
 * The one phone appearance sheet, mounted with the other hosted dialogs. It
 * reads the entity LIVE from the app data (the menus' `saveAppearance`
 * refetches it), falling back to the snapshot the opener carried, and wires
 * the same live-save pair the menus' desktop submenus use.
 */
export function AppearanceSheetMount() {
  const { open, payload, setOpen } = appearanceSheet.useDialogState();
  const { workflows, teams } = useAppData();
  const workflowActions = useWorkflowMenuActions();
  const teamActions = useTeamMenuActions();

  if (payload === null) return null;
  const { target } = payload;

  if (target.kind === "workflow") {
    const { workflowId } = target.workflow;
    const row =
      workflows.find((w) => w.workflowId === workflowId) ?? target.workflow;
    const icon = toIconName(row.icon?.icon) ?? ENTITY_KIND_ICON.workflow;
    const color = toColor(row.icon?.color) ?? "blue";
    return (
      <VisualPickerSheet
        color={color}
        defaultIcon={ENTITY_KIND_ICON.workflow}
        icon={icon}
        onColorChange={(next) =>
          void workflowActions.saveAppearance(workflowId, icon, next)
        }
        onIconChange={(next) =>
          void workflowActions.saveAppearance(workflowId, next, color)
        }
        onOpenChange={setOpen}
        // Reset is offered only while the glyph has left the kind's own —
        // the same gate as the menu's submenu.
        onReset={
          icon === ENTITY_KIND_ICON.workflow
            ? undefined
            : () =>
                void workflowActions.saveAppearance(
                  workflowId,
                  ENTITY_KIND_ICON.workflow,
                  color,
                )
        }
        open={open}
      />
    );
  }

  const { teamId } = target.team;
  const row = teams.find((t) => t.teamId === teamId) ?? target.team;
  const glyph = resolveTeamGlyph(row);
  // Monogram mode: the grid leads with "Use initial", which reports `null`.
  // Each write re-sends the other half verbatim so a colour pick never
  // converts a monogram team into a glyph team.
  return (
    <VisualPickerSheet
      color={glyph.color}
      icon={glyph.icon}
      monogram={row.teamName}
      onColorChange={(next) =>
        void teamActions.saveAppearance(teamId, glyph.icon, next)
      }
      onIconChange={(next) =>
        void teamActions.saveAppearance(teamId, next, glyph.color)
      }
      onOpenChange={setOpen}
      onReset={
        glyph.icon === null
          ? undefined
          : () => void teamActions.saveAppearance(teamId, null, glyph.color)
      }
      open={open}
    />
  );
}
